import { flatMap } from 'lodash'
import moment from 'moment-timezone'
import { rrulestr } from 'rrule'

import { DEFAULT_TIME_ZONE } from 'civic-champs-shared/utils/format'
import { RawEvent, RawEventRegistrations, RawEventResponse } from '../hooks/useGetRawEvents'
import { OpportunityStatus } from 'Event/interfaces/interfaceCreateEditEvent'
import { OpportunityApiResponse } from 'Event/interfaces'

const buildRegistrationCountMaps = (registrations: RawEventRegistrations[] = []) => {
  const roleCounts = new Map<string, number>()
  const shiftCounts = new Map<string, number>()

  for (const { opportunityId, dates, roleIds, timeshiftIds, count } of registrations) {
    for (let i = 0; i < dates.length; i++) {
      const roleKey = `${opportunityId}:${dates[i]}:${roleIds[i]}`
      roleCounts.set(roleKey, (roleCounts.get(roleKey) || 0) + count[i])

      if (timeshiftIds?.length && timeshiftIds[i] != null) {
        const shiftKey = `${opportunityId}:${dates[i]}:${timeshiftIds[i]}:${roleIds[i]}`
        shiftCounts.set(shiftKey, (shiftCounts.get(shiftKey) || 0) + count[i])
      }
    }
  }

  return { roleCounts, shiftCounts }
}

const mapTimeshifts = (
  rawEvent: RawEvent,
  date: string,
  roleCounts: Map<string, number>,
  shiftCounts: Map<string, number>,
) => {
  const { occurrenceId, opportunityId, shiftStart, shiftEnd, roles, shifts } = rawEvent

  if (!shifts?.length) {
    return [
      {
        opportunity_occurrence_id: occurrenceId,
        time_start: shiftStart,
        time_end: shiftEnd,
        roles: roles.map(({ id, name, available }) => ({
          id,
          name,
          all: available, //TODO this is SUCH a bad name...
          available: available - (roleCounts.get(`${opportunityId}:${date}:${id}`) || 0),
        })),
      },
    ]
  }

  return shifts.map(shift => ({
    id: shift.id,
    name: shift.name,
    opportunity_occurrence_id: occurrenceId,
    time_start: shift.timeStart,
    time_end: shift.timeEnd,
    roles: shift.roles.map(({ id, name, available }) => ({
      id,
      name,
      all: available,
      available: available - (shiftCounts.get(`${opportunityId}:${date}:${shift.id}:${id}`) || 0),
    })),
  }))
}

export const mapRawEventsResponse = (rawEventsResponse: RawEventResponse): Partial<OpportunityApiResponse>[] => {
  const { organization, events, registrations } = rawEventsResponse

  const timeZone = organization.timeZone || DEFAULT_TIME_ZONE
  const { roleCounts, shiftCounts } = buildRegistrationCountMaps(registrations)

  return flatMap(events, rawEvent => {
    const { occurrenceId, opportunityId, status, name, description, shiftStart, shiftEnd, recurrencePattern } = rawEvent

    const duration = moment(shiftEnd, 'LT').diff(moment(shiftStart, 'LT'))

    const dates = []
    try {
      dates.push(...rrulestr(recurrencePattern, { forceset: true }).all())
    } catch (err) {
      console.error(`Skipping occurrence (id:${occurrenceId}), invalid recurrence pattern "${recurrencePattern}"`)
    }

    return dates.map(date => {
      const startsAt = moment(date).utc(false).tz(timeZone, true)
      const dateKey = startsAt.format('YYYY-MM-DD')

      return {
        id: Number(`-${startsAt.format('YYYYMMDD')}${opportunityId}`),
        occurrenceId,
        opportunityId,
        organization,
        startsAt: startsAt.toDate(),
        endsAt: startsAt.clone().add(duration).toDate(),
        duration: moment.duration(duration).minutes(),
        name,
        description,
        is_recurring: true,
        schedulable: true,
        isOffer: false,
        trackSignUps: false,
        timeshifts: mapTimeshifts(rawEvent, dateKey, roleCounts, shiftCounts),
        status,
        published: status !== OpportunityStatus.Draft,
      }
    })
  })
}
